var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a;
import { Component, Vue, Prop } from 'vue-property-decorator';
import DataTable from '@/components/data-table/DataTable';
import CreatePlanDialog from './CreatePlanDialog/CreatePlanDialog';
import { StudentDocument, StudentDocumentIncludes } from '@/models/StudentDocument';
import ErrorHandler from '@/support/ErrorHandler';
import { formatDate } from '@/support/String';
import { RolesEnum } from '@/items/Roles';
import { Group } from '@/models/Group';
import AddDocumentDialog, { DocumentEntityType, } from '@/components/AddDocumentDialog/AddDocumentDialog.vue';
let Plan = class Plan extends Vue {
    constructor() {
        // File structure for Typescript files in your Vue project
        // https://tutorials.atabix.com/frontend/typscript_file_structure/
        super(...arguments);
        // #endregion
        // #region @Refs
        // #endregion
        // #region Class properties
        this.$pageTitle = 'Student Plan';
        this.isCreatingPlan = false;
        this.selectedPlan = null;
        this.DocumentEntityType = DocumentEntityType;
        // #endregion
        // #region @Watchers
        // #endregion
    }
    // #endregion
    // #region Lifecycle Hooks / Init
    mounted() {
        this.initialize();
    }
    async initialize() {
        if (this.$route.query.document) {
            const document = await new StudentDocument()
                .include([
                StudentDocumentIncludes.STATUS,
                StudentDocumentIncludes.TEACHERS,
                StudentDocumentIncludes.CREATED_BY,
            ])
                .find(this.$route.query.document);
            if (document) {
                this.selectedPlan = document;
            }
        }
    }
    // #endregion
    // #region Class methods
    refreshDataTable() {
        document.dispatchEvent(new CustomEvent('datatable:hook', {
            detail: (dataTable) => {
                dataTable.refresh();
            },
        }));
    }
    editPlan(plan) {
        this.selectedPlan = plan;
    }
    onCreated() {
        this.refreshDataTable();
        this.closeDialog();
    }
    onUpdated() {
        this.refreshDataTable();
        this.closeDialog();
    }
    closeDialog() {
        this.isCreatingPlan = false;
        this.selectedPlan = null;
    }
    openConfirmDeleteDialog(plan) {
        this.$store.dispatch('openDialog', this.dialogOptions(plan));
    }
    onSubmitted() {
        this.isPlanDialogOpen = false;
        this.$store.dispatch('openDialog', this.successDialogOptions);
        this.refreshDataTable();
    }
    // #endregion
    // #region Async methods
    // #endregion
    // #region Getters & Setters
    get isPlanDialogOpen() {
        return !!(this.selectedPlan || this.isCreatingPlan);
    }
    set isPlanDialogOpen(value) {
        this.closeDialog();
    }
    get canAddPlan() {
        return this.$store.state.Auth.hasRole([
            RolesEnum.ADMIN,
            RolesEnum.ROOT,
            RolesEnum.TEACHER,
            RolesEnum.TRAINER,
            RolesEnum.MANAGER,
        ]);
    }
    getStatusLabel(plan) {
        return plan.is_draft ? this.$tc('student_plan.status.concept') : this.$tc('student_plan.status.published');
    }
    get tableOptions() {
        return {
            model: new StudentDocument()
                .include([
                StudentDocumentIncludes.STATUS,
                StudentDocumentIncludes.TEACHERS,
                StudentDocumentIncludes.CREATED_BY,
                StudentDocumentIncludes.BLOCKS,
                StudentDocumentIncludes.STUDENT_SUBJECTS,
            ])
                .filter({ group: this.group.id }),
            sort: {
                key: 'start_at',
                order: 'DESC',
            },
            name: {
                singular: this.$tc('student_plan.title_group', 1),
                plural: this.$tc('student_plan.title_group', 2),
            },
            headers: [
                {
                    text: this.$tc('student_plan.table.title'),
                    value: 'title',
                },
                {
                    text: this.$tc('student_plan.table.description'),
                    value: 'description',
                    transform: (description) => {
                        return description || '-';
                    },
                },
                {
                    text: this.$tc('student_plan.table.start_at'),
                    value: 'start_at',
                    transform: (date) => {
                        return date ? formatDate(date, 'dd-LL-yyyy') : '-';
                    },
                    sortable: {
                        key: 'start_at',
                        order: 'ASC',
                    },
                },
                {
                    text: this.$tc('student_plan.table.end_at'),
                    value: 'end_at',
                    transform: (date) => {
                        return date ? formatDate(date, 'dd-LL-yyyy') : '-';
                    },
                    sortable: {
                        key: 'end_at',
                        order: 'ASC',
                    },
                },
                {
                    text: this.$tc('student_plan.table.status'),
                    value: 'is_draft',
                    transform: (_is_draft, plan) => {
                        return this.getStatusLabel(plan);
                    },
                    sortable: {
                        key: 'is_draft',
                        order: 'ASC',
                    },
                },
            ],
            actions: [
                {
                    type: 'edit',
                    label: 'Download',
                    icon: 'download',
                    action: async (vm, plan) => {
                        if (plan && plan.has_file) {
                            const document = await new StudentDocument()
                                .include(StudentDocumentIncludes.FILE)
                                .find(plan.id);
                            window.open(document.file || '', '_blank');
                        }
                    },
                    visible: (vm, plan) => {
                        return !!(plan && plan.has_file) && this.canViewDocument(plan);
                    },
                },
                {
                    type: 'edit',
                    label: 'edit',
                    icon: 'edit',
                    action: (vm, plan) => this.editPlan(plan),
                    visible: (vm, plan) => {
                        return this.canEditDocument(plan);
                    },
                },
                {
                    type: 'delete',
                    label: 'delete',
                    icon: 'delete',
                    action: (vm, plan) => {
                        this.openConfirmDeleteDialog(plan);
                    },
                    visible: (vm, plan) => {
                        return this.isDocumentOwner(plan);
                    },
                },
            ],
            filters: [],
        };
    }
    get user() {
        return this.$store.state.Auth;
    }
    get currentUserId() {
        return this.user?.id;
    }
    get isRoot() {
        return this.user?.hasRole(RolesEnum.ROOT) || false;
    }
    get isAdminOrRoot() {
        return this.user?.hasRole([RolesEnum.ROOT, RolesEnum.ADMIN]) || false;
    }
    get isTeacherWithExpertRights() {
        return this.user && this.user.isTeacherWithExpertRights();
    }
    isDocumentOwner(plan) {
        return this.currentUserId === plan.created_by?.id || this.isRoot;
    }
    isTeacherOnDocument(plan, permission) {
        const teachers = plan.teachers || [];
        return teachers.some((teacher) => teacher.user?.id === this.currentUserId &&
            (!permission || teacher.permission === permission));
    }
    canViewDocument(plan) {
        if (this.isAdminOrRoot)
            return true;
        if (this.isDocumentOwner(plan))
            return true;
        if (this.isTeacherWithExpertRights)
            return true;
        if (plan.is_visible_for_teachers || plan.is_editable_for_teachers)
            return true;
        return this.isTeacherOnDocument(plan);
    }
    canEditDocument(plan) {
        if (this.isAdminOrRoot)
            return true;
        if (this.isDocumentOwner(plan))
            return true;
        if (plan.is_editable_for_teachers)
            return true;
        return this.isTeacherOnDocument(plan, 'edit');
    }
    get tableVisibility() {
        return {
            checkboxes: false,
        };
    }
    get successDialogOptions() {
        return {
            title: this.$tc('Success'),
            text: this.$tc('Document successfully edited'),
            type: 'success',
            buttons: {
                confirm: {
                    text: this.$tc('dialogOptions.button.ok'),
                },
            },
        };
    }
    dialogOptions(plan) {
        return {
            title: this.$t('dialogOptions.confirmation').toString(),
            text: this.$tc('dialogOptions.delete', 1, {
                item: this.$t('dialogOptions.item.project'),
            }).toString(),
            type: 'warning',
            buttons: {
                confirm: {
                    text: this.$t('dialogOptions.button.delete').toString(),
                    action: () => {
                        plan
                            .delete()
                            .then((response) => {
                            if (response) {
                                this.refreshDataTable();
                            }
                        })
                            .catch((error) => {
                            ErrorHandler.network(error);
                        });
                    },
                },
                cancel: {
                    text: this.$t('dialogOptions.button.cancel').toString(),
                },
            },
        };
    }
};
__decorate([
    Prop({ required: true }),
    __metadata("design:type", typeof (_a = typeof Group !== "undefined" && Group) === "function" ? _a : Object)
], Plan.prototype, "group", void 0);
Plan = __decorate([
    Component({
        components: {
            DataTable,
            CreatePlanDialog,
            AddDocumentDialog,
        },
    })
], Plan);
export default Plan;
